import { useState, useCallback } from 'react';
import axios from '../../../../utils/axios';
import useAuth from '../../../../hooks/useAuth';
import { HOST_API } from '../../../../config';

export default function useDex() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const hasDexAccess = useCallback(() => {
    if (!user || !user.orgPlan?.features || user.role !== 'org') return false;
    return user.orgPlan.features.some((f) => f.slug === 'dex');
  }, [user]);

  // List conversations. Pass a mode to keep coaching threads out of a list the
  // user reads as their assistant history.
  const getConversations = useCallback(async ({ mode } = {}) => {
    try {
      const res = await axios.get('/api/v1/dex/admin/conversations/', {
        params: mode ? { mode } : undefined,
      });
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err?.response?.data?.error || err?.message };
    }
  }, []);

  // Get conversation with messages
  const getConversation = useCallback(async (conversationId) => {
    try {
      const res = await axios.get(`/api/v1/dex/admin/conversations/${conversationId}/`);
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err?.response?.data?.error || err?.message };
    }
  }, []);

  // Create new conversation.
  //
  // `mode` is fixed here and never changes afterwards. 'assistant' answers from
  // the org's own data and costs credits; 'coach' teaches the product, reads no
  // tenant data and is free. The backend enforces the difference; passing the
  // mode is how the browser asks for the free one.
  const createConversation = useCallback(async ({ mode } = {}) => {
    try {
      const res = await axios.post('/api/v1/dex/admin/conversations/', mode ? { mode } : {});
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err?.response?.data?.error || err?.message };
    }
  }, []);

  // Rename or archive conversation
  const updateConversation = useCallback(async (conversationId, updates) => {
    try {
      const res = await axios.patch(`/api/v1/dex/admin/conversations/${conversationId}/`, updates);
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err?.response?.data?.error || err?.message };
    }
  }, []);

  // Delete conversation
  const deleteConversation = useCallback(async (conversationId) => {
    try {
      await axios.delete(`/api/v1/dex/admin/conversations/${conversationId}/`);
      return { success: true };
    } catch (err) {
      return { success: false, error: err?.response?.data?.error || err?.message };
    }
  }, []);

  // Send message (non-streaming)
  const sendMessage = useCallback(async (conversationId, message) => {
    setLoading(true);
    try {
      const res = await axios.post(
        `/api/v1/dex/admin/conversations/${conversationId}/messages/`,
        { message }
      );
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err?.response?.data?.error || err?.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Stream message — calls onEvent for each SSE event, returns a Promise
  const streamMessage = useCallback(async (conversationId, message, onEvent, signal) => {
    const accessToken = localStorage.getItem('accessToken');

    const url = `${HOST_API}/api/v1/dex/admin/conversations/${conversationId}/messages/stream/`;

    let response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ message }),
        signal,
      });
    } catch (err) {
      if (err.name === 'AbortError') { onEvent({ type: 'cancelled' }); return; }
      onEvent({ type: 'error', message: err.message });
      return;
    }

    if (!response.ok) {
      onEvent({ type: 'error', message: `Request failed: ${response.status}` });
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const pump = async () => {
      let chunk;
      try {
        chunk = await reader.read();
      } catch (err) {
        if (err.name === 'AbortError') { onEvent({ type: 'cancelled' }); return; }
        throw err;
      }
      const { done, value } = chunk;
      if (done) return;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      lines
        .filter((line) => line.startsWith('data: '))
        .forEach((line) => {
          try {
            onEvent(JSON.parse(line.slice(6)));
          } catch {
            // skip malformed chunk
          }
        });

      await pump();
    };

    await pump();
  }, []);

  // Confirm a write action
  const confirmWrite = useCallback(async (conversationId, functionName, args) => {
    try {
      const res = await axios.post(
        `/api/v1/dex/admin/conversations/${conversationId}/write-confirm/`,
        { function: functionName, args }
      );
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err?.response?.data?.error || err?.message };
    }
  }, []);

  return {
    hasDexAccess,
    getConversations,
    getConversation,
    createConversation,
    updateConversation,
    deleteConversation,
    sendMessage,
    streamMessage,
    confirmWrite,
    loading,
  };
}
